// src/store/slices/settingsSlice.ts - Settings state management

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc 
} from 'firebase/firestore';
import { firestore } from '../../firebase/config';

// Define types
interface Settings {
  userId: string;
  theme: 'light' | 'dark';
  notifications: boolean;
  defaultReflectionDepth: number;
  privacySettings: {
    shareData: boolean;
    allowAnalytics: boolean;
  };
}

interface SettingsState {
  settings: Settings | null;
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: SettingsState = {
  settings: null,
  isLoading: false,
  error: null,
};

// Default settings
const defaultSettings: Omit<Settings, 'userId'> = {
  theme: 'dark',
  notifications: true,
  defaultReflectionDepth: 2,
  privacySettings: {
    shareData: false,
    allowAnalytics: true,
  },
};

// Async thunks
export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (userId: string, { rejectWithValue, dispatch }) => {
    try {
      const settingsRef = doc(firestore, 'userSettings', userId);
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        return settingsSnap.data() as Settings;
      } else {
        // Create default settings if none exist
        const newSettings: Settings = {
          userId,
          ...defaultSettings,
        };
        
        await dispatch(createSettings(newSettings));
        return newSettings;
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createSettings = createAsyncThunk(
  'settings/createSettings',
  async (settingsData: Settings, { rejectWithValue }) => {
    try {
      const settingsRef = doc(firestore, 'userSettings', settingsData.userId);
      
      await setDoc(settingsRef, settingsData);
      
      return settingsData;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateSettings = createAsyncThunk(
  'settings/updateSettings',
  async ({ userId, updates }: { userId: string; updates: Partial<Settings> }, { rejectWithValue }) => {
    try {
      const settingsRef = doc(firestore, 'userSettings', userId);
      
      await updateDoc(settingsRef, updates);
      
      return updates;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Create slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    resetSettings: (state) => {
      state.settings = null;
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch settings
    builder.addCase(fetchSettings.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchSettings.fulfilled, (state, action) => {
      state.isLoading = false;
      state.settings = action.payload;
    });
    builder.addCase(fetchSettings.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Create settings
    builder.addCase(createSettings.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createSettings.fulfilled, (state, action) => {
      state.isLoading = false;
      state.settings = action.payload;
    });
    builder.addCase(createSettings.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Update settings
    builder.addCase(updateSettings.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateSettings.fulfilled, (state, action) => {
      state.isLoading = false;
      if (state.settings) {
        state.settings = {
          ...state.settings,
          ...action.payload,
        };
      }
    });
    builder.addCase(updateSettings.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const { resetSettings } = settingsSlice.actions;
export default settingsSlice.reducer;
